import cn from 'classnames';
import { ReactNode } from 'react';

import styles from './ChipGroup.module.scss';

export type ChipGroupItem<Value extends string> = {
  value: Value;
  label: ReactNode;
};

// `chips` wrap onto the next row when space ends (filters, a block's type);
// `segmented` keeps a few options in one pill (a view switch).
export type ChipGroupVariant = 'chips' | 'segmented';

type ChipGroupProps<Value extends string> = {
  items: ChipGroupItem<Value>[];
  value: Value;
  label: string;
  variant?: ChipGroupVariant;
  className?: string;
  testID?: string;
  disabled?: boolean;
  onChange: (value: Value) => void;
};

// One choice among a few options shown at once: a radio group drawn as chips.
export const ChipGroup = <Value extends string>({
  items,
  value,
  label,
  variant = 'chips',
  className,
  testID,
  disabled,
  onChange,
}: ChipGroupProps<Value>) => (
  <div
    role="radiogroup"
    aria-label={label}
    className={cn(styles.group, styles[variant], className)}
    data-testid={testID}
  >
    {items.map((item) => (
      <button
        key={item.value}
        type="button"
        role="radio"
        aria-checked={item.value === value}
        className={cn('p4', styles.chip)}
        disabled={disabled}
        onClick={() => onChange(item.value)}
      >
        {item.label}
      </button>
    ))}
  </div>
);
